import React, { useCallback, useEffect, useState } from 'react';
import API from '../../services/api';
import Icon from '../../components/ui/Icon';
import Avatar from '../../components/ui/Avatar';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import { SkeletonCard } from '../../components/ui/Skeleton';
import './Leaderboard.css';

const LIMIT = 25;
const MEDAL = { 1: 'gold', 2: 'silver', 3: 'bronze' };
const LEVEL_LABEL = { top_performer: 'Top performer', placement_ready: 'Placement ready', moderate: 'Moderate', needs_improvement: 'Needs improvement' };

// College-scoped leaderboard (GET /api/leaderboard — server/controllers/leaderboardController.js).
// An admin sees only their own college; a super admin must pick exactly one college — there is
// deliberately no merged, cross-college ranking anywhere in this screen.
export default function AdminLeaderboard({ isSuper = false }) {
  const [colleges, setColleges] = useState(null);
  const [collegeId, setCollegeId] = useState('');
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(!isSuper);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isSuper) return;
    API.get('/colleges').then(res => setColleges(res.data || [])).catch(() => setColleges([]));
  }, [isSuper]);

  const load = useCallback(() => {
    if (isSuper && !collegeId) { setBoard(null); return; }
    setLoading(true); setError(null);
    API.get('/leaderboard', { params: { college_id: isSuper ? collegeId : undefined, page, limit: LIMIT } })
      .then(res => setBoard(res.data))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [isSuper, collegeId, page]);

  useEffect(() => { load(); }, [load]);

  const pickCollege = (id) => { setCollegeId(id); setPage(1); };

  return (
    <div className="z-page z-page--narrow lbx-page">
      <div className="z-section-head">
        <div>
          <h1 className="z-section-title">Leaderboard</h1>
          <p className="z-section-sub">Ranked by real readiness scores — same signal as each student's own progress screen. One college at a time; rankings never mix colleges.</p>
        </div>
      </div>

      {isSuper && (
        <div className="z-field lbx-college-picker">
          <label className="z-label" htmlFor="lbx-college">College</label>
          <select id="lbx-college" className="z-select" value={collegeId} onChange={(e) => pickCollege(e.target.value)}>
            <option value="">Select a college…</option>
            {(colleges || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {isSuper && !collegeId && (
        <EmptyState icon="trophy" art={false} tone="neutral" title="Select a college" body="Choose a college above to see its leaderboard." />
      )}

      {(!isSuper || collegeId) && loading && (
        <div role="status" aria-label="Loading leaderboard"><SkeletonCard lines={6} /></div>
      )}

      {(!isSuper || collegeId) && !loading && error && <ErrorState error={error} onRetry={load} />}

      {(!isSuper || collegeId) && !loading && !error && board && (
        <>
          <div className="lbx-summary">
            <span className="lbx-summary-college">{board.college_name}</span>
            <span className="lbx-summary-meta">{board.total_ranked} ranked of {board.student_count} students</span>
          </div>

          {board.entries.length === 0 ? (
            <EmptyState icon="trophy" art={false} tone="neutral" title="No one is ranked yet" body="Rankings appear once students have a completed, scored assessment." />
          ) : (
            <ul className="lbx-list">
              {board.entries.map(e => (
                <li key={e.user_id} className={`z-surface lbx-row lbx-row--${MEDAL[e.rank] || 'plain'}`}>
                  <span className="lbx-rank">{MEDAL[e.rank] ? <Icon name="trophy" size={20} /> : `#${e.rank}`}</span>
                  <Avatar avatarId={e.avatar_id} name={e.name} size="sm" />
                  <span className="lbx-name">{e.name}</span>
                  <span className="lbx-score" title="Placement readiness score — the number this ranking is based on">
                    <b>{Math.round(e.readiness_score)}</b> <small>{LEVEL_LABEL[e.readiness_level] || 'Readiness'}{e.provisional ? ' · provisional' : ''}</small>
                  </span>
                  <span className="lbx-level" title="Player level (practice engagement, not used for ranking)">Lv {e.level}</span>
                  <span className="lbx-xp">{e.xp.toLocaleString()} XP</span>
                </li>
              ))}
            </ul>
          )}

          {board.total_pages > 1 && (
            <div className="lbx-paging">
              <button type="button" className="z-btn z-btn--secondary z-btn--sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icon name="chevron-left" size={16} /> Prev</button>
              <span className="lbx-page-info">Page {board.page} of {board.total_pages}</span>
              <button type="button" className="z-btn z-btn--secondary z-btn--sm" disabled={page >= board.total_pages} onClick={() => setPage(p => p + 1)}>Next <Icon name="chevron-right" size={16} /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
